import { useEffect } from 'react';
import {
  getAnamnesis,
  postAnamnesis,
  selectAnamnesis,
  selectAnamnesisState,
  selectSickLeaveAndPrescriptionStatus,
  setAnamnasisState,
} from '../../store/slices/sickLeaveAndPrescription';
import { ContentViewer } from '../../components/ContentViewer/ContentViewer';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { useConsultationContext } from '../../context/ConsultationContext';
import { selectDirtyState, selectPersonLoggedIn, setDirtyState } from '../../store/slices/auth';

import { toast } from 'react-toastify';
import AnamnesisForm from './AnamnesisForm';
import WithLoader from '../../components/WithLoader/WithLoader';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import Modal from '../../components/UI/Modals/Modal';
import styles from './Anamnesis.module.scss';
import { useTranslation } from '../../hooks/useTranslations';

export default function Anamnesis({
  anamnesisType,
  personId,
  personExternalId,
}: {
  anamnesisType: 'consultation' | 'customer';
  personId: number;
  personExternalId: string | number;
}) {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const language = localStorage.getItem('lang');
  const { selectedSalesOrder, deleteTab } = useConsultationContext();
  const logedInPerson = useAppSelector(selectPersonLoggedIn);

  const anamnesisResult = useAppSelector(selectAnamnesis);
  const anamnesisState = useAppSelector(selectAnamnesisState);
  const anamnesisStatus = useAppSelector(selectSickLeaveAndPrescriptionStatus);
  const distyState = useAppSelector(selectDirtyState);

  useEffect(() => {
    if (anamnesisResult.length === 0) {
      const anamnesisCongfig = {
        personId: personExternalId?.toString() || '',
        // workOrderId: selectedSalesOrder?.work_order?.work_order_id,
        ta_id: selectedSalesOrder?.sales_order?.ta_id,
        product_order_id: selectedSalesOrder?.sales_order?.sales_order_external_id,
        product_order_item_id: selectedSalesOrder?.sales_order_item?.external_sales_order_item_id,
        language: language || 'en',
      };
      if (anamnesisType === 'customer') {
        delete anamnesisCongfig['ta_id'];
        delete anamnesisCongfig['product_order_id'];
        delete anamnesisCongfig['product_order_item_id'];
      }
      dispatch(getAnamnesis(anamnesisCongfig));
    }
  }, []);

  const onChange = (
    groupId: string,
    question_id: string,
    value: string,
    checkbox: boolean | null
  ) => {
    dispatch(setAnamnasisState({ groupId, question_id, value, checkbox }));
    dispatch(setDirtyState({ isDirty: true, isModalOpen: false, component: 'anamnesis' }));
  };

  const handleSubmit = async () => {
    const mandatoryQuestions = anamnesisState.filter((el) => el.is_mandatory_question === true);

    const answers = anamnesisState.map((el) => {
      return {
        question_id: el.question_id,
        answer_comment: el.answer_comment || '',
        answer: el.answer,
      };
    });
    const allMandatoryQuestionsAnswered = mandatoryQuestions.every((el) => {
      return answers.find(
        (answer) => answer.question_id === el.question_id && answer.answer !== null
      );
    });

    if (allMandatoryQuestionsAnswered) {
      const state = {
        merchantPersonId: logedInPerson.person_id,
        customerPersonId: personId?.toString(),
        workOrderId: selectedSalesOrder?.work_order?.work_order_id,
        answers: answers,
      };
      await dispatch(postAnamnesis(state));
      const anamnesisCongfig = {
        personId: personExternalId?.toString() || '',
        // workOrderId: selectedSalesOrder?.work_order?.work_order_id,
        ta_id: selectedSalesOrder?.sales_order?.ta_id,
        product_order_id: selectedSalesOrder?.sales_order?.sales_order_external_id,
        product_order_item_id: selectedSalesOrder?.sales_order_item?.external_sales_order_item_id,
        language: language || 'en',
      };
      if (anamnesisType === 'customer') {
        delete anamnesisCongfig['ta_id'];
        delete anamnesisCongfig['product_order_id'];
        delete anamnesisCongfig['product_order_item_id'];
      }
      dispatch(getAnamnesis(anamnesisCongfig));
      dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'anamnesis' }));
    } else {
      toast.error('Please answer all mandatory questions', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const handleDiscardChanges = (e: any) => {
    dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'anamnesis' }));
    deleteTab(
      e,
      {
        value: 16,
        text: translations('anamnesis'),
        isVisible: true,
      },
      false
    );
    const anamnesisCongfig = {
      personId: personExternalId?.toString() || '',
      // workOrderId: selectedSalesOrder?.work_order?.work_order_id,
      ta_id: selectedSalesOrder?.sales_order?.ta_id,
      product_order_id: selectedSalesOrder?.sales_order?.sales_order_external_id,
      product_order_item_id: selectedSalesOrder?.sales_order_item?.external_sales_order_item_id,
      language: language || 'en',
    };

    if (anamnesisType === 'customer') {
      delete anamnesisCongfig['ta_id'];
      delete anamnesisCongfig['product_order_id'];
      delete anamnesisCongfig['product_order_item_id'];
    }
    // anamnesisType === 'customer' && delete anamnesisCongfig['workOrderId'];
    dispatch(getAnamnesis(anamnesisCongfig));
  };

  const handleSave = (e: any) => {
    handleSubmit();
    deleteTab(
      e,
      {
        value: 16,
        text: translations('anamnesis'),
        isVisible: true,
      },
      false
    );
  };

  return (
    <WithLoader isLoading={anamnesisStatus === 'pending'}>
      <div className={styles.anamnesis}>
        {anamnesisResult.map((group: any) => {
          const isOptional = group.anamnesis_questions.every(
            (obj: any) => obj.is_mandatory_question === false
          );

          return (
            <ContentViewer
              key={group.anamnesis_question_group_id}
              title={`${group.anamnesis_question_group_name}`}
              isCollapsible
              content=""
              isOptional={isOptional}
              style={{ marginBottom: '10px' }}
            >
              <div className={styles.group}>
                {group.anamnesis_questions.map((el: any) => {
                  return (
                    <AnamnesisForm
                      key={el.question_id}
                      onChange={onChange}
                      answer={
                        anamnesisState.find((state) => state.question_id === el.question_id)
                          ?.answer_comment || ''
                      }
                      groupId={group.anamnesis_question_group_id}
                      question={el.question}
                      question_id={el.question_id}
                      label={el.question_label}
                      answerCheckbox={
                        anamnesisState.find((state) => state.question_id === el.question_id)?.answer
                      }
                      required={isOptional ? true : el.is_mandatory_question}
                    />
                  );
                })}
              </div>
            </ContentViewer>
          );
        })}
        <div className={styles.btnWrap}>
          <PrimaryButtonV2
            onClick={handleSubmit}
            buttonStyle={{
              marginTop: 10,
            }}
            text={translations('save')}
          />
        </div>
      </div>
      <Modal
        isOpen={distyState.isModalOpen}
        onClose={() =>
          dispatch(setDirtyState({ isDirty: true, isModalOpen: false, component: 'anamnesis' }))
        }
        containerPadding={'40px 0px'}
        hideCloseButton
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginTop: 10 }}>Save your changes before leaving?</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <PrimaryButtonV2
            text={translations('save')}
            containerStyle={{ marginRight: 20 }}
            buttonStyle={{ height: 40, width: 130 }}
            onClick={handleSave}
            disabled={false}
          />
          <PrimaryButtonV2
            text={translations('Discard')}
            invertedColors
            buttonStyle={{ height: 40, width: 130 }}
            onClick={handleDiscardChanges}
            disabled={false}
          />
        </div>
      </Modal>
    </WithLoader>
  );
}
